import React, { useState, useMemo } from 'react';
import { BarChart3, Copy, Check, ExternalLink, Code, Globe } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const PERIODS = [
  { value: 'weekly', label: 'Per uke (siste 12)' },
  { value: 'monthly', label: 'Per måned (siste 12)' },
  { value: 'quarterly', label: 'Per kvartal (siste 8)' },
  { value: 'halfyearly', label: 'Per halvår (siste 6)' },
  { value: 'yearly', label: 'Per år (siste 5)' },
  { value: 'all', label: 'Alle år' },
];

/**
 * StatsEmbedSection
 * Vises kun for admin/super_admin på Innstillinger-tab.
 * Lar admin generere kopierbare iframe-snippets og API-endepunkt-lenker for
 * anonymisert oppmøtestatistikk som kan legges inn på klubbens nettside.
 */
const StatsEmbedSection = ({ organizationId, isAdmin }) => {
  const [period, setPeriod] = useState('monthly');
  const [theme, setTheme] = useState('dark');
  const [mode, setMode] = useState('iframe'); // 'iframe' eller 'api'
  const [copiedKey, setCopiedKey] = useState(null);

  // URL-er
  const iframeUrl = useMemo(() => {
    if (!organizationId) return '';
    return `${API_BASE}/embed/stats/${organizationId}?period=${period}&theme=${theme}`;
  }, [organizationId, period, theme]);

  const apiUrl = useMemo(() => {
    if (!organizationId) return '';
    return `${API_BASE}/api/public/stats/${organizationId}?period=${period}`;
  }, [organizationId, period]);

  const iframeSnippet = useMemo(() => {
    return `<iframe
  src="${iframeUrl}"
  width="100%"
  height="400"
  frameborder="0"
  title="Oppmøtestatistikk"
  loading="lazy"
></iframe>`;
  }, [iframeUrl]);

  const fetchSnippet = useMemo(() => {
    return `// JavaScript / fetch
fetch('${apiUrl}')
  .then(r => r.json())
  .then(data => {
    // data = { organization_name, period, labels[], values[], total, generated_at }
    console.log('Totale oppmøter:', data.total);
  });`;
  }, [apiUrl]);

  const copy = (text, key) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 2000);
    });
  };

  if (!isAdmin) return null;

  return (
    <div className="bg-gray-800 rounded-lg p-3 md:p-6" data-testid="stats-embed-section">
      <h3 className="text-lg font-semibold flex items-center gap-2 mb-2 text-white">
        <BarChart3 className="w-5 h-5 text-yellow-400" />
        Innebygd statistikk på klubbens nettside
      </h3>
      <p className="text-sm text-gray-400 mb-5">
        Vis anonymisert oppmøtestatistikk på klubbens nettside via iFrame eller hent ut data via API.
        Ingen personlige opplysninger eksponeres – kun aggregerte oppmøtetall.
      </p>

      {/* Konfigurasjon */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
        <div>
          <label className="block text-sm text-gray-300 mb-1">Periode</label>
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
            data-testid="stats-period-select"
          >
            {PERIODS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
        </div>

        <div>
          <label className="block text-sm text-gray-300 mb-1">Tema (iframe)</label>
          <select
            value={theme}
            onChange={(e) => setTheme(e.target.value)}
            disabled={mode === 'api'}
            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500 disabled:opacity-50"
          >
            <option value="dark">Mørk (svart bakgrunn)</option>
            <option value="light">Lys (hvit bakgrunn)</option>
          </select>
        </div>

        <div>
          <label className="block text-sm text-gray-300 mb-1">Type</label>
          <div className="flex gap-2 bg-gray-700 p-1 rounded-lg">
            <button
              type="button"
              onClick={() => setMode('iframe')}
              className={`flex-1 px-3 py-1.5 rounded text-sm font-medium transition-colors ${mode === 'iframe' ? 'bg-yellow-500 text-gray-900' : 'text-gray-300 hover:text-white'}`}
              data-testid="stats-mode-iframe"
            >
              <Globe className="w-4 h-4 inline mr-1" /> iFrame
            </button>
            <button
              type="button"
              onClick={() => setMode('api')}
              className={`flex-1 px-3 py-1.5 rounded text-sm font-medium transition-colors ${mode === 'api' ? 'bg-yellow-500 text-gray-900' : 'text-gray-300 hover:text-white'}`}
              data-testid="stats-mode-api"
            >
              <Code className="w-4 h-4 inline mr-1" /> API
            </button>
          </div>
        </div>
      </div>

      {/* iFrame mode */}
      {mode === 'iframe' && (
        <div className="space-y-4">
          {/* Preview */}
          <div className="border border-gray-600 rounded-lg overflow-hidden">
            <div className="bg-gray-900 px-3 py-2 text-xs text-gray-400 border-b border-gray-700 flex items-center justify-between">
              <span>Forhåndsvisning</span>
              <a
                href={iframeUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-yellow-400 hover:underline flex items-center gap-1"
              >
                Åpne i ny fane <ExternalLink className="w-3 h-3" />
              </a>
            </div>
            <iframe
              src={iframeUrl}
              width="100%"
              height="380"
              title="Stats preview"
              className="block"
            />
          </div>

          {/* Snippet */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-sm text-gray-300 font-medium">Kopier denne kodebiten til klubbens nettside:</label>
              <button
                onClick={() => copy(iframeSnippet, 'iframe')}
                className="text-xs px-3 py-1 bg-yellow-500 hover:bg-yellow-400 text-gray-900 rounded flex items-center gap-1 font-medium"
                data-testid="stats-copy-iframe"
              >
                {copiedKey === 'iframe' ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                {copiedKey === 'iframe' ? 'Kopiert!' : 'Kopier'}
              </button>
            </div>
            <pre className="bg-gray-900 border border-gray-700 rounded-lg p-3 text-xs text-yellow-200 overflow-x-auto whitespace-pre-wrap break-all">
              {iframeSnippet}
            </pre>
          </div>

          <div className="bg-blue-900/20 border border-blue-500/30 rounded p-3 text-xs text-blue-200">
            <strong>Slik bruker du:</strong> Lim inn kodebiten i HTML-koden på klubbens nettside der du vil at statistikken skal vises. Den vil oppdateres automatisk – endre {`{period}`} for å vise andre tidsrom.
          </div>
        </div>
      )}

      {/* API mode */}
      {mode === 'api' && (
        <div className="space-y-4">
          {/* Endpoint URL */}
          <div>
            <label className="block text-sm text-gray-300 font-medium mb-1">API-endepunkt (offentlig, ingen auth)</label>
            <div className="flex items-center gap-2 bg-gray-900 border border-gray-700 rounded-lg p-2">
              <code className="flex-1 text-xs text-yellow-200 truncate" data-testid="stats-api-url">{apiUrl}</code>
              <button
                onClick={() => copy(apiUrl, 'apiurl')}
                className="text-xs px-2 py-1 bg-yellow-500 hover:bg-yellow-400 text-gray-900 rounded flex items-center gap-1 font-medium"
              >
                {copiedKey === 'apiurl' ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                {copiedKey === 'apiurl' ? 'Kopiert!' : 'Kopier'}
              </button>
            </div>
          </div>

          {/* Eksempel-respons */}
          <div>
            <label className="block text-sm text-gray-300 font-medium mb-1">Eksempel-respons (JSON)</label>
            <pre className="bg-gray-900 border border-gray-700 rounded-lg p-3 text-xs text-green-300 overflow-x-auto">
{`{
  "organization_name": "Din klubb",
  "period": "${period}",
  "labels": ["Jan 2026", "Feb 2026", ...],
  "values": [42, 38, 51, ...],
  "total": 245,
  "generated_at": "2026-02-13T10:00:00+01:00"
}`}
            </pre>
          </div>

          {/* JS eksempel */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-sm text-gray-300 font-medium">Eksempel: JavaScript / fetch</label>
              <button
                onClick={() => copy(fetchSnippet, 'fetch')}
                className="text-xs px-3 py-1 bg-yellow-500 hover:bg-yellow-400 text-gray-900 rounded flex items-center gap-1 font-medium"
              >
                {copiedKey === 'fetch' ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                {copiedKey === 'fetch' ? 'Kopiert!' : 'Kopier'}
              </button>
            </div>
            <pre className="bg-gray-900 border border-gray-700 rounded-lg p-3 text-xs text-yellow-200 overflow-x-auto">
              {fetchSnippet}
            </pre>
          </div>

          <div className="bg-blue-900/20 border border-blue-500/30 rounded p-3 text-xs text-blue-200">
            <strong>Tips:</strong> Du kan bruke <code className="bg-gray-700 px-1 rounded">period</code>-parameteren med følgende verdier:
            <code className="bg-gray-700 px-1 mx-1 rounded">weekly</code>,
            <code className="bg-gray-700 px-1 mx-1 rounded">monthly</code>,
            <code className="bg-gray-700 px-1 mx-1 rounded">quarterly</code>,
            <code className="bg-gray-700 px-1 mx-1 rounded">halfyearly</code>,
            <code className="bg-gray-700 px-1 mx-1 rounded">yearly</code>,
            <code className="bg-gray-700 px-1 mx-1 rounded">all</code>.
            Endepunktet returnerer alltid kun aggregerte tall — aldri personopplysninger.
          </div>
        </div>
      )}
    </div>
  );
};

export default StatsEmbedSection;
